import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import axios from "axios";
import { API_URL } from "../../../../config";
import {
  mapCombinedSalesDocRows,
  summarizeWrittenSales,
  formatCurrency,
} from "./writtenSalesHelpers";
import "./WrittenSales.css";

// "Written Sales" widget — written Sales (excl. tax) from combined sales
// documents, for Month-To-Date and Year-To-Date. Same as the Revenue & Profit
// widget but without the profit half.
//
// Role behaviour follows the same dataScope contract as the other widgets
// (no role names hardcoded):
//   dataScope === "self" (Sales): only the logged-in salesperson's written
//     sales — Written Sales MTD/YTD KPI cards.
//   dataScope === "all" (Sales Director / President): overall totals + a
//     per-salesperson breakdown table.
//
// Live data comes from the CombinedSalesDocs endpoint; each row is mapped
// (mapCombinedSalesDocRows) into the record shape summarizeWrittenSales
// consumes, so the aggregation and UI below stay source-agnostic.

const ZERO_TOTALS = { salesMtd: 0, salesYtd: 0 };

// Data layer: pull combined sales-document rows from the dashboard endpoint and
// normalize them for the aggregation. `salesPersonId` (Sales / self scope)
// narrows to a single rep; `signal` aborts the in-flight request on unmount.
const fetchWrittenSales = async ({ token, salesPersonId, signal }) => {
  const res = await axios.get(
    `${API_URL}/Dashboard/api/Dashboard/CombinedSalesDocs`,
    { headers: { Authorization: `Bearer ${token}` }, signal }
  );
  let tmpRes = res && res.data ? res.data.result : []
  tmpRes = tmpRes.filter(
  item =>
    item.division?.trim().toLowerCase() !== "commercial" &&
    item.salespersonName?.trim().toLowerCase() !== "robert hempel"
  ); // remove commercial plans as well rober hampel
  const records = mapCombinedSalesDocRows(tmpRes);

  return records;
  // return salesPersonId
  //   ? records.filter((r) => r.salesPerson === salesPersonId)
  //   : records;
};

const WrittenSales = ({ auth: { auth }, dataScope = "all", userId }) => {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const token = auth && auth[0] ? auth[0].token : null;
  const isSelf = dataScope === "self";
  const salesPerson = auth && auth[0] ? auth[0].userName : null;
  const salesPersonId = isSelf ? salesPerson : null;

  useEffect(() => {
    if (!token) return undefined;
    const controller = new AbortController();
    let cancelled = false;
    setLoading(true);
    setError(false);
    fetchWrittenSales({ token, salesPersonId, signal: controller.signal })
      .then((records) => {
        if (cancelled) return;
        setSummary(summarizeWrittenSales(records));
      })
      .catch((err) => {
        if (cancelled || axios.isCancel(err)) return;
        console.warn("Could not load Written Sales:", err.message);
        setError(true);
        setSummary(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [token, salesPersonId]);

  const totals = summary ? summary.totals : ZERO_TOTALS;
  const reps = summary ? summary.reps : [];

  const card = (label, value) => (
    <div className="ws-card">
      <div className="ws-card-value">{formatCurrency(value)}</div>
      <div className="ws-card-label">{label}</div>
    </div>
  );

  // Written Sales KPI group (used by both roles for the top section).
  const totalsSection = (
    <div className="ws-groups">
      <div className="ws-group">
        <div className="ws-group-title">Written Sales</div>
        <div className="ws-cards">
          {card("Month To Date", totals.salesMtd)}
          {card("Year To Date", totals.salesYtd)}
        </div>
      </div>
    </div>
  );

  return (
    <div className="ws-widget">
      <h4 className="ws-title">Written Sales</h4>

      {loading ? (
        <div className="ws-state">Loading…</div>
      ) : error ? (
        <div className="ws-state ws-error">Unable to load data.</div>
      ) : isSelf ? (
        // Sales: own Written Sales KPI cards.
        totalsSection
      ) : (
        // Sales Director / President: overall totals + per-rep breakdown.
        <div className="ws-overview">
          {totalsSection}
          <table className="ws-table">
            <thead>
              <tr>
                <th className="ws-rowhead">Salesperson</th>
                <th>Written MTD</th>
                <th>Written YTD</th>
              </tr>
            </thead>
            <tbody>
              {reps.length > 0 ? (
                reps.map((rep) => (
                  <tr key={rep.repName}>
                    <td className="ws-rep">{rep.repName}</td>
                    <td className="ws-num">{formatCurrency(rep.salesMtd)}</td>
                    <td className="ws-num">{formatCurrency(rep.salesYtd)}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={3} className="ws-empty">
                    No written sales.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const mapStateToProps = ({ auth }) => ({ auth });

export { WrittenSales };

export default connect(mapStateToProps)(WrittenSales);
